(function (root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.TserraDisplayCurrency = api;

    if (root.document) {
      const start = function () {
        api.init(root);
      };

      if (root.document.readyState === 'loading') {
        root.document.addEventListener('DOMContentLoaded', start, { once: true });
      } else {
        start();
      }
    }
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  const SUPPORTED_CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'SGD', 'NZD'];
  const RATE_URL =
    'https://api.frankfurter.dev/v2/rates?base=USD&quotes=EUR,GBP,CAD,AUD,JPY,SGD,NZD';
  const STORAGE_CURRENCY = 'tserraDisplayCurrency';
  const STORAGE_RATES = 'tserraDisplayCurrencyRates';
  const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
  const USD_TOKEN = /(?:US)?\$\s*(\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?|\d+(?:\.\d{1,2})?)(?:\s*USD)?/g;
  const HAS_USD_TOKEN = /(?:US)?\$\s*(?:\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?|\d+(?:\.\d{1,2})?)(?:\s*USD)?/;
  const PRICE_SELECTOR = [
    '.price-item',
    '.product-option',
    '.totals__total-value',
    '.price-per-item--current',
    '.unit-price'
  ].join(',');
  const LOCALES = {
    USD: 'en-US',
    EUR: 'de-DE',
    GBP: 'en-GB',
    CAD: 'en-CA',
    AUD: 'en-AU',
    JPY: 'ja-JP',
    SGD: 'en-SG',
    NZD: 'en-NZ'
  };

  function isSupportedCurrency(currency) {
    return SUPPORTED_CURRENCIES.includes(currency);
  }

  function isValidRate(rate) {
    return Number.isFinite(rate) && rate > 0;
  }

  function normalizeRateRows(rows) {
    const rates = { USD: 1 };

    if (!Array.isArray(rows)) {
      return rates;
    }

    rows.forEach(function (row) {
      if (
        row &&
        row.base === 'USD' &&
        isSupportedCurrency(row.quote) &&
        row.quote !== 'USD' &&
        isValidRate(row.rate)
      ) {
        rates[row.quote] = row.rate;
      }
    });

    return rates;
  }

  function formatMoney(amount, currency) {
    if (!Number.isFinite(amount) || !isSupportedCurrency(currency)) {
      return '';
    }

    const decimals = currency === 'JPY' ? 0 : 2;
    const formatted = new Intl.NumberFormat(LOCALES[currency], {
      style: 'currency',
      currency: currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(amount);

    return formatted + ' ' + currency;
  }

  function replaceUsdText(text, rate, currency) {
    const original = String(text);

    if (currency === 'USD' || !isSupportedCurrency(currency) || !isValidRate(rate)) {
      return original;
    }

    USD_TOKEN.lastIndex = 0;
    return original.replace(USD_TOKEN, function (_match, rawAmount) {
      const amount = Number(rawAmount.replace(/,/g, ''));

      if (!Number.isFinite(amount)) {
        return _match;
      }

      return formatMoney(amount * rate, currency);
    });
  }

  function storageGet(windowObject, key) {
    try {
      return windowObject.localStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  }

  function storageSet(windowObject, key, value) {
    try {
      windowObject.localStorage.setItem(key, value);
    } catch (_error) {
      // Storage is optional; the current page still remains functional.
    }
  }

  function hasCompleteRateSet(rates) {
    return SUPPORTED_CURRENCIES.every(function (currency) {
      return isValidRate(rates && rates[currency]);
    });
  }

  async function loadRates(windowObject) {
    const cachedValue = storageGet(windowObject, STORAGE_RATES);

    if (cachedValue) {
      try {
        const cached = JSON.parse(cachedValue);
        if (
          cached &&
          Number.isFinite(cached.timestamp) &&
          Date.now() - cached.timestamp < CACHE_TTL_MS &&
          hasCompleteRateSet(cached.rates)
        ) {
          return cached.rates;
        }
      } catch (_error) {
        // Ignore an invalid cache and request fresh rates.
      }
    }

    try {
      const response = await windowObject.fetch(RATE_URL, {
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        return { USD: 1 };
      }

      const rates = normalizeRateRows(await response.json());
      if (!hasCompleteRateSet(rates)) {
        return { USD: 1 };
      }

      storageSet(
        windowObject,
        STORAGE_RATES,
        JSON.stringify({ timestamp: Date.now(), rates: rates })
      );
      return rates;
    } catch (_error) {
      return { USD: 1 };
    }
  }

  function init(windowObject) {
    const document = windowObject.document;

    if (!document || windowObject.__tserraDisplayCurrencyInitialized) {
      return;
    }

    windowObject.__tserraDisplayCurrencyInitialized = true;

    let currentCurrency = 'USD';
    let currentRates = { USD: 1 };
    let observerTimer;

    function getPriceElements() {
      return Array.from(document.querySelectorAll(PRICE_SELECTOR)).filter(function (element) {
        return element.children.length === 0;
      });
    }

    function applyToElement(element) {
      const liveText = element.textContent || '';

      if (HAS_USD_TOKEN.test(liveText)) {
        element.dataset.tserraUsdText = liveText;
      }

      const usdText = element.dataset.tserraUsdText;
      if (!usdText) {
        return;
      }

      const output =
        currentCurrency === 'USD'
          ? usdText
          : replaceUsdText(usdText, currentRates[currentCurrency], currentCurrency);

      if (element.textContent !== output) {
        element.textContent = output;
      }
    }

    function applyPrices() {
      getPriceElements().forEach(applyToElement);
    }

    function getSelectors() {
      return Array.from(document.querySelectorAll('[data-tserra-currency-selector]'));
    }

    function syncSelectors() {
      getSelectors().forEach(function (selector) {
        selector.value = currentCurrency;
      });
    }

    async function setCurrency(requestedCurrency) {
      let nextCurrency = isSupportedCurrency(requestedCurrency) ? requestedCurrency : 'USD';

      if (nextCurrency !== 'USD') {
        const loadedRates = await loadRates(windowObject);
        if (!isValidRate(loadedRates[nextCurrency])) {
          nextCurrency = 'USD';
          currentRates = { USD: 1 };
        } else {
          currentRates = loadedRates;
        }
      }

      currentCurrency = nextCurrency;
      storageSet(windowObject, STORAGE_CURRENCY, currentCurrency);
      syncSelectors();
      applyPrices();
    }

    function bindSelectors() {
      getSelectors().forEach(function (selector) {
        if (selector.dataset.tserraCurrencyBound === 'true') {
          return;
        }

        selector.dataset.tserraCurrencyBound = 'true';
        selector.addEventListener('change', function (event) {
          setCurrency(event.target.value);
        });
      });
      syncSelectors();
    }

    function scheduleRefresh() {
      windowObject.clearTimeout(observerTimer);
      observerTimer = windowObject.setTimeout(function () {
        bindSelectors();
        applyPrices();
      }, 50);
    }

    bindSelectors();

    const storedCurrency = storageGet(windowObject, STORAGE_CURRENCY);
    setCurrency(isSupportedCurrency(storedCurrency) ? storedCurrency : 'USD');

    if (windowObject.MutationObserver && document.body) {
      const observer = new windowObject.MutationObserver(scheduleRefresh);
      observer.observe(document.body, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    document.addEventListener('shopify:section:load', scheduleRefresh);
  }

  return {
    SUPPORTED_CURRENCIES: SUPPORTED_CURRENCIES,
    normalizeRateRows: normalizeRateRows,
    formatMoney: formatMoney,
    replaceUsdText: replaceUsdText,
    init: init
  };
});
